import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { WritingChunks } from '@/types/frontend-types';
import {
  startCbullshitcodingAnimationLoop,
  type CbullshitcodingAnimationLoopInput,
  type CbullshitcodingAnimationTimingEmitter,
  type CbullshitcodingAnimationTimingEventInput,
} from './CbullshitcodingAnimationLoop';
import { getWordCount, type DisplayLine } from './Cbullshitcoding.typing';
import type { CbullshitcodingTimingEvent } from './Cbullshitcoding.types';
import type { CbullshitcodingTypingState } from './useCbullshitcodingTypingState';

interface UseCbullshitcodingAnimationInput {
  strings: DisplayLine[];
  animationResetKey: string | DisplayLine[];
  isFadingOut: boolean;
  writingChunks: WritingChunks;
  speed: number;
  loop: boolean;
  clearOnEnd: boolean;
  charTypeSpeedMs: number;
  wordTypeSpeedMs: number;
  pauseBetweenLinesMs: number;
  trailingLineDropCount: number;
  trailingLineDropPauseMs: number;
  onCycleComplete?: () => void;
  onTimingEvent?: (event: CbullshitcodingTimingEvent) => void;
  typingState: CbullshitcodingTypingState;
}

interface TimingEventRuntime {
  stringCount: number;
  writingChunks: WritingChunks;
  charTypeSpeedMs: number;
  wordTypeSpeedMs: number;
  pauseBetweenLinesMs: number;
}

interface AnimationRuntimeRefs {
  cycleStartedAtRef: MutableRefObject<number | null>;
  lineStartedAtRef: MutableRefObject<number | null>;
  onCycleCompleteRef: MutableRefObject<(() => void) | undefined>;
  onTimingEventRef: MutableRefObject<CbullshitcodingTimingEventHandler>;
  stringsRef: MutableRefObject<DisplayLine[]>;
}

interface AnimationRuntimeRefsInput {
  strings: DisplayLine[];
  onCycleComplete: (() => void) | undefined;
  onTimingEvent: CbullshitcodingTimingEventHandler;
}

interface AnimationLoopEffectInput {
  animation: UseCbullshitcodingAnimationInput;
  refs: AnimationRuntimeRefs;
}

interface EffectiveTypingSpeeds {
  effectiveCharTypeSpeedMs: number;
  effectiveWordTypeSpeedMs: number;
}

interface StartAnimationLoopIfReadyInput extends EffectiveTypingSpeeds {
  stringsRef: MutableRefObject<DisplayLine[]>;
  isFadingOut: boolean;
  writingChunks: WritingChunks;
  loop: boolean;
  clearOnEnd: boolean;
  pauseBetweenLinesMs: number;
  trailingLineDropCount: number;
  trailingLineDropPauseMs: number;
  onCycleCompleteRef: MutableRefObject<(() => void) | undefined>;
  onTimingEventRef: MutableRefObject<CbullshitcodingTimingEventHandler>;
  cycleStartedAtRef: MutableRefObject<number | null>;
  lineStartedAtRef: MutableRefObject<number | null>;
  controlsRef: CbullshitcodingTypingState['controlsRef'];
  hasContentRef: CbullshitcodingTypingState['hasContentRef'];
  resetTypingState: CbullshitcodingTypingState['resetTypingState'];
  setCompletedLines: CbullshitcodingTypingState['setCompletedLines'];
  setCurrentLineIndex: CbullshitcodingTypingState['setCurrentLineIndex'];
  setCurrentTypingLine: CbullshitcodingTypingState['setCurrentTypingLine'];
  stringIndexRef: CbullshitcodingTypingState['stringIndexRef'];
  timeoutRef: CbullshitcodingTypingState['timeoutRef'];
}

interface AnimationLoopInputFactoryInput extends StartAnimationLoopIfReadyInput {
  activeStrings: DisplayLine[];
  emitTimingEvent: CbullshitcodingAnimationTimingEmitter;
}

type CbullshitcodingTimingEventHandler =
  | ((event: CbullshitcodingTimingEvent) => void)
  | undefined;

function useLatestValueRef<Value>(value: Value): MutableRefObject<Value> {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  return valueRef;
}

function useAnimationRuntimeRefs(
  input: AnimationRuntimeRefsInput,
): AnimationRuntimeRefs {
  const cycleStartedAtRef = useRef<number | null>(null);
  const lineStartedAtRef = useRef<number | null>(null);
  const onCycleCompleteRef = useLatestValueRef(input.onCycleComplete);
  const onTimingEventRef = useLatestValueRef(input.onTimingEvent);
  const stringsRef = useLatestValueRef(input.strings);

  return {
    cycleStartedAtRef,
    lineStartedAtRef,
    onCycleCompleteRef,
    onTimingEventRef,
    stringsRef,
  };
}

function getElapsedMs(input: {
  startedAt: number | null;
  timestampMs: number;
}): number | null {
  return input.startedAt == null ? null : input.timestampMs - input.startedAt;
}

function getLineLength(input: { line?: string }): number | null {
  return input.line?.length ?? null;
}

function getTimingWordCount(input: { line?: string }): number | null {
  if (!input.line) {
    return null;
  }

  return getWordCount(input.line);
}

function buildTimingEvent(input: {
  event: CbullshitcodingAnimationTimingEventInput;
  runtime: TimingEventRuntime;
  lineStartedAt: number | null;
  cycleStartedAt: number | null;
}): CbullshitcodingTimingEvent {
  return {
    type: input.event.type,
    timestampMs: input.event.timestampMs,
    stringCount: input.runtime.stringCount,
    writingChunks: input.runtime.writingChunks,
    charTypeSpeedMs: input.runtime.charTypeSpeedMs,
    wordTypeSpeedMs: input.runtime.wordTypeSpeedMs,
    pauseBetweenLinesMs: input.runtime.pauseBetweenLinesMs,
    stringIndex: input.event.stringIndex ?? null,
    lineLength: getLineLength({ line: input.event.line }),
    wordCount: getTimingWordCount({ line: input.event.line }),
    lineElapsedMs: getElapsedMs({
      startedAt: input.lineStartedAt,
      timestampMs: input.event.timestampMs,
    }),
    cycleElapsedMs: getElapsedMs({
      startedAt: input.cycleStartedAt,
      timestampMs: input.event.timestampMs,
    }),
  };
}

function createTimingRuntime(input: {
  stringCount: number;
  writingChunks: WritingChunks;
  effectiveCharTypeSpeedMs: number;
  effectiveWordTypeSpeedMs: number;
  pauseBetweenLinesMs: number;
}): TimingEventRuntime {
  return {
    stringCount: input.stringCount,
    writingChunks: input.writingChunks,
    charTypeSpeedMs: input.effectiveCharTypeSpeedMs,
    wordTypeSpeedMs: input.effectiveWordTypeSpeedMs,
    pauseBetweenLinesMs: input.pauseBetweenLinesMs,
  };
}

function createTimingEventEmitter(input: {
  onTimingEventRef: MutableRefObject<CbullshitcodingTimingEventHandler>;
  runtime: TimingEventRuntime;
  lineStartedAtRef: MutableRefObject<number | null>;
  cycleStartedAtRef: MutableRefObject<number | null>;
}): CbullshitcodingAnimationTimingEmitter {
  return (event): void => {
    const onTimingEventHandler = input.onTimingEventRef.current;
    if (!onTimingEventHandler) {
      return;
    }

    onTimingEventHandler(buildTimingEvent({
      event,
      runtime: input.runtime,
      lineStartedAt: input.lineStartedAtRef.current,
      cycleStartedAt: input.cycleStartedAtRef.current,
    }));
  };
}

function getEffectiveTypingSpeeds(input: {
  charTypeSpeedMs: number;
  wordTypeSpeedMs: number;
  speed: number;
}): EffectiveTypingSpeeds {
  return {
    effectiveCharTypeSpeedMs: input.charTypeSpeedMs * input.speed,
    effectiveWordTypeSpeedMs: input.wordTypeSpeedMs * input.speed,
  };
}

function createAnimationLoopInput(
  input: AnimationLoopInputFactoryInput,
): CbullshitcodingAnimationLoopInput {
  return {
    activeStrings: input.activeStrings,
    writingChunks: input.writingChunks,
    loop: input.loop,
    clearOnEnd: input.clearOnEnd,
    effectiveCharTypeSpeedMs: input.effectiveCharTypeSpeedMs,
    effectiveWordTypeSpeedMs: input.effectiveWordTypeSpeedMs,
    pauseBetweenLinesMs: input.pauseBetweenLinesMs,
    trailingLineDropCount: input.trailingLineDropCount,
    trailingLineDropPauseMs: input.trailingLineDropPauseMs,
    onCycleCompleteRef: input.onCycleCompleteRef,
    emitTimingEvent: input.emitTimingEvent,
    cycleStartedAtRef: input.cycleStartedAtRef,
    lineStartedAtRef: input.lineStartedAtRef,
    controlsRef: input.controlsRef,
    hasContentRef: input.hasContentRef,
    resetTypingState: input.resetTypingState,
    setCompletedLines: input.setCompletedLines,
    setCurrentLineIndex: input.setCurrentLineIndex,
    setCurrentTypingLine: input.setCurrentTypingLine,
    stringIndexRef: input.stringIndexRef,
    timeoutRef: input.timeoutRef,
  };
}

function startAnimationLoopIfReady(
  input: StartAnimationLoopIfReadyInput,
): (() => void) | undefined {
  if (input.isFadingOut) {
    return undefined;
  }

  const activeStrings = input.stringsRef.current;
  if (activeStrings.length === 0) {
    return undefined;
  }

  const timingRuntime = createTimingRuntime({
    stringCount: activeStrings.length,
    writingChunks: input.writingChunks,
    effectiveCharTypeSpeedMs: input.effectiveCharTypeSpeedMs,
    effectiveWordTypeSpeedMs: input.effectiveWordTypeSpeedMs,
    pauseBetweenLinesMs: input.pauseBetweenLinesMs,
  });
  const emitTimingEvent = createTimingEventEmitter({
    onTimingEventRef: input.onTimingEventRef,
    runtime: timingRuntime,
    lineStartedAtRef: input.lineStartedAtRef,
    cycleStartedAtRef: input.cycleStartedAtRef,
  });

  return startCbullshitcodingAnimationLoop(createAnimationLoopInput({
    ...input,
    activeStrings,
    emitTimingEvent,
  }));
}

function useAnimationLoopEffect(input: AnimationLoopEffectInput): void {
  const { animationResetKey, isFadingOut, writingChunks, speed, loop, clearOnEnd, charTypeSpeedMs, wordTypeSpeedMs, pauseBetweenLinesMs, trailingLineDropCount, trailingLineDropPauseMs, typingState } = input.animation;
  const { cycleStartedAtRef, lineStartedAtRef, onCycleCompleteRef, onTimingEventRef, stringsRef } = input.refs;
  const { controlsRef, hasContentRef, resetTypingState, setCompletedLines, setCurrentLineIndex, setCurrentTypingLine, stringIndexRef, timeoutRef } = typingState;
  const { effectiveCharTypeSpeedMs, effectiveWordTypeSpeedMs } = getEffectiveTypingSpeeds({ charTypeSpeedMs, wordTypeSpeedMs, speed });

  useEffect(() => startAnimationLoopIfReady({
    stringsRef, isFadingOut, writingChunks, loop, clearOnEnd, effectiveCharTypeSpeedMs, effectiveWordTypeSpeedMs, pauseBetweenLinesMs, trailingLineDropCount, trailingLineDropPauseMs, onCycleCompleteRef, onTimingEventRef, cycleStartedAtRef, lineStartedAtRef, controlsRef, hasContentRef, resetTypingState, setCompletedLines, setCurrentLineIndex, setCurrentTypingLine, stringIndexRef, timeoutRef,
  }), [animationResetKey, charTypeSpeedMs, clearOnEnd, controlsRef, cycleStartedAtRef, effectiveCharTypeSpeedMs, effectiveWordTypeSpeedMs, hasContentRef, isFadingOut, lineStartedAtRef, loop, onCycleCompleteRef, onTimingEventRef, pauseBetweenLinesMs, resetTypingState, setCompletedLines, setCurrentLineIndex, setCurrentTypingLine, speed, stringIndexRef, stringsRef, timeoutRef, trailingLineDropCount, trailingLineDropPauseMs, wordTypeSpeedMs, writingChunks]);
}

export function useCbullshitcodingAnimation(
  input: UseCbullshitcodingAnimationInput,
): void {
  const refs = useAnimationRuntimeRefs({
    strings: input.strings,
    onCycleComplete: input.onCycleComplete,
    onTimingEvent: input.onTimingEvent,
  });

  useAnimationLoopEffect({ animation: input, refs });
}
